import React from 'react';
import FormField from '../shared/FormField';
import formDataStorage from '../../utils/formDataStorage';
import { AlertCircle, Shield, Gavel, Lock, DollarSign, Users, Truck } from 'lucide-react';

const Part2Form = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('Part2Form useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('part2');
    console.log('Part2Form useEffect - 已保存数据:', savedData);
    console.log('Part2Form useEffect - 当前props数据:', data);
    
    // 检查是否缺少 assisted_severe_trafficking 字段
    if (!data || !data.assisted_severe_trafficking) {
      console.log('⚠️ Part2Form - 缺少 assisted_severe_trafficking 字段，正在初始化...');
      onChange('assisted_severe_trafficking', {
        value: false,
        explanation: ''
      });
    }
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('Part2Form useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]); // 依赖data和onChange
  // 问题列表
  const questions = [
    {
      key: 'arrested',
      label: '您是否曾经因违法或犯罪被捕或被判刑，即使后来您受到了宽恕、赦免或其它类似的裁决？',
      explanationKey: 'arrested_explanation',
      icon: Lock,
      iconColor: 'text-red-600'
    },
    {
      key: 'controlled_substances',
      label: '您是否曾经违反或密谋违反有关管控物资方面的法律？',
      explanationKey: 'controlled_substances_explanation',
      icon: Shield,
      iconColor: 'text-orange-600'
    },
    {
      key: 'prostitution',
      label: '您是来美国从事卖淫或非法商业性交易吗？在过去十年中，您是否从事过卖淫或组织介绍过卖淫？',
      explanationKey: 'prostitution_explanation',
      icon: Users,
      iconColor: 'text-purple-600'
    },
    {
      key: 'money_laundering',
      label: '您是否曾经参与或意图从事洗钱活动？',
      explanationKey: 'money_laundering_explanation',
      icon: DollarSign,
      iconColor: 'text-green-600'
    },
    {
      key: 'human_trafficking',
      label: '您曾在美国或美国以外的地方犯有或密谋人口走私罪吗？',
      explanationKey: 'human_trafficking_explanation',
      icon: Truck,
      iconColor: 'text-blue-600'
    },
    {
      key: 'assisted_severe_trafficking',
      label: '你有没有故意资助，教唆，协助或勾结某个人，而这个人在美国或美国以外的地方曾犯有、或密谋了一严重的人口走私案？',
      explanationKey: 'assisted_severe_trafficking_explanation',
      icon: Users,
      iconColor: 'text-indigo-600'
    },
    {
      key: 'human_trafficking_related',
      label: '您是一曾在美国或美国以外犯有或密谋人口走私案犯的配偶或儿女吗？您在最近5年里是否从走私活动中获得过好处？',
      explanationKey: 'human_trafficking_related_explanation',
      icon: Users,
      iconColor: 'text-pink-600'
    }
  ];

  // 获取问题的当前值
  const getQuestionValue = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'value' in questionData) {
      return questionData.value;
    }
    // 兼容旧的数据格式
    if (data[key] !== undefined) {
      return data[key];
    }
    // 如果数据未初始化，默认返回 false
    return false;
  };

  // 获取问题的解释文本
  const getQuestionExplanation = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'explanation' in questionData) {
      return questionData.explanation || '';
    }
    return '';
  };

  // 处理是否选择的变化
  const handleYesNoChange = (key, value) => {
    const boolValue = value === 'true';
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新value字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        value: boolValue,
        explanation: boolValue ? currentData.explanation : ''
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: boolValue,
        explanation: ''
      };
      onChange(key, newData);
    }
  };

  // 处理解释文本的变化
  const handleExplanationChange = (key, explanation) => {
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新explanation字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        explanation: explanation
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: currentData,
        explanation: explanation
      };
      onChange(key, newData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">⚖️</span>
          法律和犯罪历史
        </h3>

        {/* 重要提示 */}
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-bold mb-2 text-base">重要提示</p>
              <p className="mb-2">以下问题涉及法律和犯罪历史。请务必如实回答所有问题。</p>
              <p className="font-semibold">任何虚假陈述都可能导致：</p>
              <ul className="list-disc list-inside mt-1 ml-2">
                <li>签证申请被拒绝</li>
                <li>面临法律起诉</li>
                <li>被禁止入境美国</li>
              </ul>
            </div>
          </div>
        </div>

        {questions.map((question, index) => {
          const questionValue = getQuestionValue(question.key);
          const questionExplanation = getQuestionExplanation(question.key);
          const IconComponent = question.icon;
          
          return (
            <div key={question.key} className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                  <IconComponent className={`w-5 h-5 mr-2 ${question.iconColor}`} />
                  {question.label}
                </h4>
                
                <div>
                  
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name={question.key}
                        value="false"
                        checked={questionValue === false}
                        onChange={(e) => {
                          handleYesNoChange(question.key, 'false');
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">❌ 否 - 我没有此类情况</span>
                    </label>
                    
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name={question.key}
                        value="true"
                        checked={questionValue === true}
                        onChange={(e) => {
                          handleYesNoChange(question.key, 'true');
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">✅ 是 - 我有此类情况</span>
                    </label>
                  </div>
                </div>
              </div>

              {questionValue === true && (
                <div className="mt-4 space-y-3">
                  {/* 强烈风险警告 */}
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                    <div className="flex items-start">
                      <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                        <p className="mb-2">
                          <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                          此选项可能导致：
                        </p>
                        <ul className="list-disc list-inside ml-2 space-y-1">
                          <li className="font-semibold">面签时遭到严格审查</li>
                          <li className="font-semibold">签证申请被拒绝</li>
                          <li className="font-semibold">可能面临永久拒签</li>
                        </ul>
                        <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                          💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                        </p>
                      </div>
                    </div>
                  </div>

                  <FormField
                    label="请详细说明相关情况"
                    type="textarea"
                    value={questionExplanation}
                    onChange={(e) => handleExplanationChange(question.key, e.target.value)}
                    placeholder="请详细说明相关情况，包括时间、地点、原因、结果等..."
                    required
                    rows={4}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* 法律提醒 */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex items-start">
            <Gavel className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-800 mb-1">法律提醒</h4>
              <p className="text-sm text-blue-700">
                以上问题涉及美国移民法相关规定。某些犯罪历史可能会影响您的签证资格，但并不意味着自动拒签。
                请确保您的回答完整、准确。如有疑问，建议咨询专业移民律师。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Part2Form; 